/**
 * What a test saw from a camera (ENG-2457 / ENG-2470): a snapshot and a
 * 5-second clip, both presigned links that expire. Used in the camera check's
 * "Each camera" table (a thumbnail that opens the clip) and in the stream
 * test's result.
 *
 * Every field is optional: results from older admin builds, and the
 * connector's own report, have no media and render as before.
 */
import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import { TsButton } from "../../Troubleshooting/TroubleshootingUi";
import { videoFacts } from "./connectivityModel";
import { CameraMedia } from "./siteConnectivityTypes";

export const MEDIA_EXPIRED_TEXT = "Run the test again to see video";
export const CLIP_TRANSCODED_TEXT =
  "Converted to H.264 so it plays in your browser";

/** The camera has a snapshot or a clip to show. */
export const hasMedia = (media: CameraMedia | null | undefined): boolean =>
  Boolean(media?.snapshot_url || media?.clip_url);

/** True once `expiresAt` has passed; flips by itself when it does. */
export const useMediaExpired = (
  expiresAt: string | null | undefined
): boolean => {
  const expiry = expiresAt ? Date.parse(expiresAt) : NaN;
  const [expired, setExpired] = React.useState(
    !Number.isNaN(expiry) && expiry <= Date.now()
  );
  React.useEffect(() => {
    if (Number.isNaN(expiry)) {
      setExpired(false);
      return undefined;
    }
    const left = expiry - Date.now();
    if (left <= 0) {
      setExpired(true);
      return undefined;
    }
    setExpired(false);
    const timer = setTimeout(() => setExpired(true), left);
    return () => clearTimeout(timer);
  }, [expiry]);
  return expired;
};

type Facts = Parameters<typeof videoFacts>[0];

/**
 * The clip (or, without one, the snapshot), the video's facts, and a note
 * when we converted it. Expired or unloadable media says to run the test
 * again, with a button for it when the caller can.
 */
export const CameraClip = ({
  media,
  facts,
  expiresAt,
  onRunAgain,
  canRunAgain = false,
}: {
  media: CameraMedia;
  facts?: Facts | null;
  /** Defaults to `media.media_expires_at`. */
  expiresAt?: string | null;
  onRunAgain?: () => void;
  canRunAgain?: boolean;
}) => {
  const clipUrl = media.clip_url ?? null;
  const snapshotUrl = media.snapshot_url ?? null;
  const expired = useMediaExpired(expiresAt ?? media.media_expires_at);
  const [broken, setBroken] = React.useState(false);
  React.useEffect(() => setBroken(false), [clipUrl, snapshotUrl]);
  const factsText = facts ? videoFacts(facts) : null;

  let picture: React.ReactNode;
  if (expired || broken) {
    picture = (
      <div
        className="flex flex-wrap items-center gap-3 rounded border border-dashed border-[#d1d5db] bg-[#f9fafb] px-4 py-6 font-mulish text-[14px] text-[#4c5257]"
        data-testid="camera-media-expired"
      >
        {MEDIA_EXPIRED_TEXT}.
        {canRunAgain && onRunAgain && (
          <TsButton size="sm" onClick={onRunAgain}>
            Run again
          </TsButton>
        )}
      </div>
    );
  } else if (clipUrl) {
    picture = (
      <video
        src={clipUrl}
        poster={snapshotUrl ?? undefined}
        controls
        muted
        loop
        playsInline
        className="block max-h-[420px] w-full rounded border border-[#d1d5db] bg-black object-contain"
        onError={() => setBroken(true)}
        data-testid="camera-media-clip"
      />
    );
  } else if (snapshotUrl) {
    picture = (
      <img
        src={snapshotUrl}
        alt="What we saw from the camera's stream"
        className="block max-h-[420px] max-w-full rounded border border-[#d1d5db] bg-black object-contain"
        onError={() => setBroken(true)}
        data-testid="camera-media-snapshot"
      />
    );
  } else {
    return null;
  }

  return (
    <div className="flex flex-col gap-2" data-testid="camera-media">
      {picture}
      {factsText && (
        <p
          className="m-0 font-mulish text-[14px] font-bold text-actuate-blue"
          data-testid="camera-media-facts"
        >
          {factsText}
        </p>
      )}
      {clipUrl && media.clip_transcoded && !expired && !broken && (
        <p
          className="m-0 font-mulish text-[13px] text-[#4c5257]"
          data-testid="camera-media-transcoded"
        >
          {CLIP_TRANSCODED_TEXT}
        </p>
      )}
    </div>
  );
};

export const CameraMediaDialog = ({
  open,
  onClose,
  cameraName,
  media,
  facts,
}: {
  open: boolean;
  onClose: () => void;
  cameraName?: string | null;
  media: CameraMedia;
  facts?: Facts | null;
}) => (
  <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
    <DialogTitle
      sx={{ fontFamily: "Mulish", fontWeight: 800, color: "#001943", pr: 6 }}
    >
      What we saw
      {cameraName ? (
        <span className="block font-mulish font-normal text-[14px] text-[#4c5257]">
          {cameraName}
        </span>
      ) : null}
      <IconButton
        aria-label="Close"
        onClick={onClose}
        sx={{ position: "absolute", right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers sx={{ position: "relative" }}>
      {open && <CameraClip media={media} facts={facts} />}
    </DialogContent>
  </Dialog>
);

/**
 * A small snapshot for the "Each camera" table; clicking it opens the clip.
 * Without a snapshot (or once it won't load) it's a play icon instead.
 */
export const CameraMediaThumb = ({
  cameraId,
  cameraName,
  media,
  facts,
}: {
  cameraId: number;
  cameraName?: string | null;
  media: CameraMedia;
  facts?: Facts | null;
}) => {
  const [open, setOpen] = React.useState(false);
  const [thumbBroken, setThumbBroken] = React.useState(false);
  if (!hasMedia(media)) return null;
  const showImage = Boolean(media.snapshot_url) && !thumbBroken;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="relative block h-[45px] w-[80px] overflow-hidden rounded border border-[#d1d5db] bg-[#f3f4f6] p-0"
        aria-label={`See what we saw from ${cameraName ?? "this camera"}`}
        data-testid={`camera-media-thumb-${cameraId}`}
      >
        {showImage ? (
          <img
            src={media.snapshot_url as string}
            alt=""
            className="block h-full w-full object-cover"
            onError={() => setThumbBroken(true)}
          />
        ) : null}
        {(media.clip_url || !showImage) && (
          <span className="absolute inset-0 flex items-center justify-center">
            <PlayCircleOutlineIcon
              sx={{
                fontSize: 22,
                color: showImage ? "#ffffff" : "#283e6d",
                filter: showImage ? "drop-shadow(0 0 2px #000)" : undefined,
              }}
              aria-hidden
            />
          </span>
        )}
      </button>
      <CameraMediaDialog
        open={open}
        onClose={() => setOpen(false)}
        cameraName={cameraName}
        media={media}
        facts={facts}
      />
    </>
  );
};
